import {
  Badge,
  Icon,
  IconSizeClasses,
  SortableTable,
  useDidMountEffect,
} from '@eppendorf/vnls-react-components';
import { ButtonV2, useBreakpoint } from '@eppendorf/vnls-react-components-preview';
import {
  Pagination as PaginationType,
  UserStatus,
  UsersSortParams,
  User,
  createUserPermissions,
  UserRole,
} from '@eppendorf/vnls-user-tenant-utils';
import { createColumnHelper } from '@tanstack/react-table';
import i18next from 'i18next';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AccessControlGuard } from '$shared/access-control/access-control-guard';
import { useAccessControlGuard } from '$shared/custom-hooks/useAccessControlGuard';
import { useCurrentUserId } from '$shared/custom-hooks/useCurrentUserId';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';
import {
  isFeatureEnabled,
  isSupportUser,
} from '$shared/feature-toggle/is-feature-enabled';

import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { Overview } from '$components/overview-box/overview-box';
import { Pagination } from '$components/table/pagination/pagination';
import { DeleteUserDialogWrapper } from '$features/delete-user';
import { InviteUserDialogWrapper } from '$features/invite-user';
import { isValueWithKeyInSearchParams } from '$features/navigation/isValueWithKeyInSearchParams';
import { stringifyUserRoles } from '$features/users/roles.utils';
import { useUsersWithQueryParams } from '$features/users/users.api';
import { BillingUserHint } from '$features/users-overview/billing-user-hint';

import './users-overview.scss';

const initialPageIndex = 1;
const initialPageSize = 10;
const billingUserPermissions = createUserPermissions([UserRole.BillingUser]);

export function getStatusDisplay(status: number | undefined, border = false) {
  switch (status) {
    case UserStatus['Invite Sent']:
      return (
        <Badge bordered={border} className="m-top-xs" color="orange">
          {i18next.t('userStatus.pending')}
        </Badge>
      );
    case UserStatus.Active:
      return (
        <Badge bordered={border} className="m-top-xs" color="green">
          {i18next.t('userStatus.enabled')}
        </Badge>
      );
    case UserStatus.Inactive:
      return <Badge>{i18next.t('userStatus.disabled')}</Badge>;
    case UserStatus.Error:
      return (
        <Badge bordered={border} className="m-top-xs" color="red">
          {i18next.t('userStatus.error')}
        </Badge>
      );
    default:
      return '';
  }
}

export function UsersOverview(): ReactElement {
  const [isSupportUserFlag, setIsSupportUserFlag] = useState(false);
  const devtoolingEnabled = isFeatureEnabled('devtooling') || isSupportUserFlag;
  const { t } = useTranslation();
  const isMinSm = useBreakpoint({ min: 'sm' });
  const [isInviteUserDialogOpen, setInviteUserDialogOpen] = useState(false);
  const [isDeleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>();

  const navigate = useNavigate();
  const [{ page, pageSize }, setPagination] = useState<PaginationType>({
    page: initialPageIndex,
    pageSize: initialPageSize,
  });

  const [sortBy, setSortBy] = useState<UsersSortParams['sort']>('+status');
  const hasPermissionToDeleteUser = useAccessControlGuard('DELETE_USER');
  const hasPermissionToViewOtherProfileDetails = useAccessControlGuard(
    'VIEW_OTHERS_PROFILE_DETAILS',
  );
  const hasPermissionToViewOwnProfileDetails = useAccessControlGuard(
    'VIEW_OWN_PROFILE_DETAILS',
  );
  const { email, tenantId, permissions } = useTokenAttributes([
    'email',
    'tenantId',
    'permissions',
  ]);
  const currentUserId = useCurrentUserId(email, tenantId);

  function handlePageSizeChange(newPageSize: string) {
    setPagination({ page: 1, pageSize: Number(newPageSize) });
  }

  function handlePageChange(newPage: number) {
    setPagination({ pageSize, page: newPage });
  }

  function handleDeleteUser(user: User) {
    setSelectedUser(user);
    setDeleteUserDialogOpen(true);
  }

  const handleUserRowClick = (user: User) => {
    if (
      hasPermissionToViewOtherProfileDetails ||
      (hasPermissionToViewOwnProfileDetails && currentUserId === user.id)
    ) {
      navigate({ search: `?sidecarRoute=user/profile&id=${user.id}` });
    }
  };

  function handleSortChange(sortKey: string) {
    if (sortBy === `+${sortKey}`) {
      setSortBy(`-${sortKey}` as UsersSortParams['sort']);
    } else {
      setSortBy(`+${sortKey}` as UsersSortParams['sort']);
    }
  }

  // Needs to be removed after the delete feature is implemented for customers.
  async function checkSupportUser() {
    try {
      const supportUserStatus = await isSupportUser();
      setIsSupportUserFlag(supportUserStatus);
    } catch (error) {
      setIsSupportUserFlag(false);
    }
  }

  const { data, isLoading, isError, refetch } = useUsersWithQueryParams({
    page,
    pageSize,
    sort: sortBy,
  });

  useDidMountEffect(() => {
    refetch();
  }, [page, pageSize, sortBy]);

  const columnHelper = createColumnHelper<User>();

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor('status', {
        id: 'status',
        header: () => t('shared.status'),
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => getStatusDisplay(info.getValue() as number),
        meta: {
          isSortable: true,
          dataLabel: t('shared.status'),
        },
      }),
      columnHelper.accessor('firstName', {
        id: 'firstName',
        header: () => t('userManagement.firstName'),
        meta: { dataLabel: t('userManagement.firstName') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => (
          <span
            className="ellipsize ellipsize--wrap-on-mobile"
            title={info.getValue() ?? ''}
          >
            {info.renderValue()}
          </span>
        ),
      }),
      columnHelper.accessor('lastName', {
        id: 'lastName',
        header: () => t('userManagement.lastName'),
        meta: { dataLabel: t('userManagement.lastName') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => (
          <span
            className="ellipsize ellipsize--wrap-on-mobile"
            title={info.getValue() ?? ''}
          >
            {info.renderValue()}
          </span>
        ),
      }),
      columnHelper.accessor('email', {
        id: 'email',
        header: () => t('shared.email'),
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => (
          <span
            className="ellipsize ellipsize--wrap-on-mobile"
            title={info.getValue() ?? ''}
          >
            {info.renderValue()}
          </span>
        ),
        meta: {
          isSortable: true,
          dataLabel: t('shared.email'),
        },
      }),
      columnHelper.accessor('roles', {
        id: 'roles',
        header: () => t('userManagement.role'),
        meta: { dataLabel: t('userManagement.role') },
        cell: (info) => stringifyUserRoles(info.getValue() || []),
      }),
    ];

    if (hasPermissionToDeleteUser && devtoolingEnabled) {
      return [
        ...cols,
        columnHelper.display({
          id: 'actions',
          header: () => t('shared.actions'),
          meta: { dataLabel: t('shared.actions') },
          // eslint-disable-next-line react/no-unstable-nested-components -- accepted
          cell: (selected) => (
            <ButtonV2
              iconOnly
              variant="ghost"
              onClick={(e) => e.stopPropagation()}
              onPress={() => handleDeleteUser(selected.row.original)}
              aria-label={t('userDelete.deleteUserAction')}
            >
              <Icon name="delete" size={IconSizeClasses.XSmall} className="bg-gray-800" />
            </ButtonV2>
          ),
        }),
      ];
    }

    return cols;
  }, [hasPermissionToDeleteUser, devtoolingEnabled, isSupportUserFlag, t]);

  const [searchParams] = useSearchParams();

  const isMaxMd = useBreakpoint({ max: 'md' });

  useEffect(() => {
    checkSupportUser();
  }, []);

  return (
    <Overview className={`box--mobile-fullscreen ${isMaxMd ? 'box--no-shadow' : ''}`}>
      {permissions === billingUserPermissions && <BillingUserHint />}
      <Overview.Header>
        <h1 className="title">{t('userManagement.overview')}</h1>
        <InviteUserDialogWrapper
          isOpen={isInviteUserDialogOpen}
          onOpenChange={(open) => setInviteUserDialogOpen(open)}
        />
        {selectedUser && (
          <DeleteUserDialogWrapper
            isOpen={isDeleteUserDialogOpen}
            selectedUser={selectedUser}
            onOpenChange={(open) => setDeleteUserDialogOpen(open)}
          />
        )}
        <AccessControlGuard requiredPermissions="INVITE_USER">
          <ButtonV2
            className={`${isMinSm ? 'p-left-sm' : ''}`}
            variant="primary"
            onPress={() => setInviteUserDialogOpen(true)}
            iconOnly={!isMinSm}
          >
            <Icon name="plus" size={IconSizeClasses.XSmall} className="bg-white" />
            {isMinSm && t('userInvite.inviteUser')}
          </ButtonV2>
        </AccessControlGuard>
      </Overview.Header>
      <Overview.Body>
        {(isLoading || isError) && <LoadingErrorHint />}

        {!isLoading && !isError && data && (
          <>
            <SortableTable<User[], User>
              data={data.data}
              columns={columns}
              onRowClick={handleUserRowClick}
              onSortChange={(sortKey) => handleSortChange(sortKey)}
              currentSortKey={sortBy}
              isSelectedRow={(user) =>
                isValueWithKeyInSearchParams('id', user.id, searchParams)
              }
              isTableToCardDesign
              className="table--user-overview"
            />
            <Pagination
              paginationData={data.pagination}
              onPageSizeChange={(size) => handlePageSizeChange(size)}
              onPageChange={(newPage) => handlePageChange(newPage)}
            />
          </>
        )}
      </Overview.Body>
    </Overview>
  );
}
